import { defineStore } from "pinia";
import { HabilitationsApi } from "@simbelapp/order-sdk";
import { useUsersHabilitationsStore } from "./users-habilitations.store";
import { useUsersHabilitationsValidation } from "~/composables/users-habilitations/useUsersHabilitations";
import type { ICreateUserHabilitation } from "~/utils/interfaces/users-interfaces";
import { HabilitationStatusEnum } from "~/utils/enums/habilitations.enums";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";

export const useCreateUserHabilitationsStore = defineStore("useCreateUserHabilitationsStore", {
  state: (): ICreateUserHabilitation => {
    return {
      user: {
        userId: null,
        habilitationId: null,
        habilitationName: null,
        habilitationStatus: HabilitationStatusEnum.COMING,
        durationYears: null,
        expirationDate: null,
        documentLink: null,
        renewalDate: null,
        toRenew: true,
        comment: null,
      },
      certificate: null,
      showCreateModal: false,
      userError: null,
      durationYearsError: null,
      habilitationError: null,
    };
  },
  getters: {},
  actions: {
    openCreateUserHabilitationsModal() {
      this.$reset();
      const uersHabilitationsStore = useUsersHabilitationsStore();
      uersHabilitationsStore.cleanValidationError();
      this.showCreateModal = true;
    },

    async validateUserHabilitationCreation() {
      const userHabilitationsValidation = useUsersHabilitationsValidation();
      const t$ = await userHabilitationsValidation.validateUsersHabilitationsCreation();
      if (t$.value.$errors) {
        this.userError = t$.value.userId.$silentErrors.map((m: any) => m.$message).join("");
        this.habilitationError = t$.value.habilitationName.$silentErrors.map((m: any) => m.$message).join("");
        this.durationYearsError = t$.value.durationYears.$silentErrors.map((m: any) => m.$message).join("");
      }
    },

    async createUserHabilitation() {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);
      try {
        if (!this.user.durationYears) return;
        const isMissing = this.user.habilitationStatus === HabilitationStatusEnum.MISSING_HABILITATION;
        const data = await apiInstance.createUserHabilitation({
          createUserHabilitationDto: {
            userId: this.user.userId!,
            habilitationId: this.user.habilitationId,
            habilitationName: this.user.habilitationName,
            habilitationStatus: this.user.habilitationStatus ?? undefined,
            durationYears: this.user.durationYears,
            expirationDate: !isMissing && this.user.expirationDate ? new Date(this.user.expirationDate) : null,
            renewalDate: !isMissing && this.user.renewalDate ? new Date(this.user.renewalDate) : null,
            toRenew: this.user.toRenew,
            comment: this.user.comment ?? null,
          },
        });

        return data;
      } catch (error: any) {
        if (error?.message?.includes("cannot create one with status Missing")) {
          this.showCreateModal = false;
          feedback.error(
            `L'habilitation ${this.user.habilitationName} existe déjà pour cet employé, 
            impossible d'en créer une au statut « Manquante »`,
            "small",
          );

          return null;
        }
        this.$reset();
        feedback.error(`Une erreur est survenue lors de la création`, "small");
        return null;
      }
    },
  },
});
